import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/auth";
import { Modal, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import Template from "../../template";
import * as S from "./styled";

function ComprarEbooks() {
  const { user, getPerfil } = useAuth();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [linkPagamento, setLinkPagamento] = useState('');
  const [pagamentos, setPagamentos] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);

  const normalizePagamentoStatus = (status) => {
    if (!status) return '';
    const upper = String(status).trim().toUpperCase();
    if (upper === 'APROVADA') return 'APROVADO';
    if (upper === 'EM PROCESSAMENTO') return 'PENDENTE';
    return upper;
  };

  const getPagamentoStatus = (pagamento) => {
    const fromStatus = normalizePagamentoStatus(pagamento?.status);
    if (fromStatus) return fromStatus;
    if (pagamento?.aprovada === true || pagamento?.aprovada === 1) return 'APROVADO';
    return 'PENDENTE';
  };

  const isReembolsado = (pagamento) => pagamento?.reembolsada === true || pagamento?.reembolsada === 1;
  const isPagamentoAprovado = (pagamento) => getPagamentoStatus(pagamento) === 'APROVADO';

  const formasPagamento = [
    { value: 'PIX', label: 'PIX'},
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito'},
    { value: 'DEBIT_CARD', label: 'Cartão de Débito'}
  ];

  // Buscar pagamentos do usuário
  const buscarPagamentos = async (isPollingCall = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await api.get('/pagamento', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.data) {
        const novosPagamentos = response.data.data;
        const pagamentosEbook = novosPagamentos.filter(p => p.plano === 'EBOOK');
        const pagamentoEbookMaisRecente = pagamentosEbook
          .slice()
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        const statusEbookMaisRecente = pagamentoEbookMaisRecente
          ? getPagamentoStatus(pagamentoEbookMaisRecente)
          : null;

        const pagamentosEbookAprovados = pagamentosEbook.filter(p =>
          isPagamentoAprovado(p) && !isReembolsado(p)
        );
        
        if (isPollingCall) {
          // Se há pagamentos aprovados e o modal não está sendo exibido, mostrar
          if (statusEbookMaisRecente === 'APROVADO' && !showConfirmationModal) {
            setShowConfirmationModal(true);
            setShowModal(false); // Fechar modal do link de pagamento
            stopPolling();
          }
          if (statusEbookMaisRecente === 'RECUSADO') {
            setError('Pagamento recusado. Tente novamente ou use outra forma de pagamento.');
            setSuccess('');
            setLinkPagamento('');
            stopPolling();
          }
        } else {
          // Carregamento inicial - verificar se há pagamentos aprovados e não reembolsados
          // Só mostrar o modal se o usuário ainda não tem o e-book no contexto
          if (pagamentosEbookAprovados.length > 0 && !user.comprou_ebook) {
            setShowConfirmationModal(true);
          }
        }
        
        setPagamentos(novosPagamentos);
      }
    } catch (err) {
      console.error('Erro ao buscar pagamentos:', err);
    }
  };

  // Iniciar polling para verificar status dinamicamente
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Já está fazendo polling
    
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(() => {
      buscarPagamentos(true);
    }, 10000); // Verificar a cada 10 segundos
  };

  // Parar polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  };

  // useEffect para buscar pagamentos ao carregar a página
  useEffect(() => {
    buscarPagamentos();
    
    // Cleanup do polling ao desmontar o componente
    return () => {
      stopPolling();
    };
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
    setError('');
    setSuccess('');
    setLinkPagamento('');
  };

  const handleCloseModal = () => {
    if (!loading) {
      setShowModal(false);
      setError('');
      setSuccess('');
      setLinkPagamento('');
    }
  };

  const handlePagamento = async (formaPagamento) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Você precisa estar logado para fazer uma compra.');
        setLoading(false);
        return;
      }

      const response = await api.post('/pagamento', {
        plano: 'EBOOK',
        forma_pagamento: formaPagamento
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.link_pagamento) {
        setLinkPagamento(response.data.link_pagamento);
        setSuccess('Link de pagamento gerado com sucesso!');
        setLoading(false);
        window.open(response.data.link_pagamento, '_blank');
        // Iniciar polling para verificar se o pagamento foi aprovado
        startPolling();
      }
    } catch (err) {
      console.error('Erro ao processar pagamento:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao processar pagamento. Tente novamente.');
      }
      setLoading(false);
    }
  };

  return (
    <Template>
      <S.EbookSection>
        <S.EbookContent>
          <S.EbookImageContainer>
            <img src="./capa_nota1000.png" alt="E-book da Clara - Nota 1000" />
          </S.EbookImageContainer>
          <S.EbookTextContent>
            <S.EbookTitle>E-book da Clara</S.EbookTitle>
            <S.EbookSubtitle>O segredo por trás da nota 1000 no ENEM 2024</S.EbookSubtitle>
            <S.EbookDescription>
              Descubra os métodos e estratégias que levaram Clara da Barra de Oliveira a conquistar a nota máxima na redação do ENEM 2024, sendo uma entre apenas 12 candidatos a alcançar esse feito extraordinário.
            </S.EbookDescription>
            <S.EbookHighlight>
              Estratégias exclusivas da aluna nota 1000
            </S.EbookHighlight>
            <S.EbookDescription>
              Neste e-book exclusivo, você terá acesso aos insights, técnicas de escrita e dicas práticas que fizeram a diferença na jornada da Clara rumo à excelência na redação do ENEM.
            </S.EbookDescription>
            {user.comprou_ebook ? (
              <S.EbookButton disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                Você já comprou este produto
              </S.EbookButton>
            ) : (
              <S.EbookButton onClick={handleOpenModal}>
                Comprar Agora
              </S.EbookButton>
            )}
          </S.EbookTextContent>
        </S.EbookContent>
      </S.EbookSection>

      {/* Modal de Seleção de Forma de Pagamento */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="payment-modal"
      >
        <div style={{
           background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
           borderRadius: '20px',
           border: 'none',
           boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 3px 10px rgba(0, 0, 0, 0.05)',
           overflow: 'hidden',
           position: 'relative'
         }}>
          {/* Header com gradiente */}
          <div style={{
            background: 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
            color: 'white',
            padding: '1.5rem 2rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
            }}></div>
            <h4 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '700',
              position: 'relative',
              zIndex: 1
            }}>Escolha a forma de pagamento</h4>
            {!loading && (
              <button
                onClick={handleCloseModal}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 2
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: '2rem' }}>
            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
                color: 'white',
                padding: '1rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                fontWeight: '500'
              }}>
                ⚠️ {error}
              </div>
            )}
            {success && (
              <div style={{
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                color: 'white',
                padding: '1rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                fontWeight: '500'
              }}>
                ✅ {success}
              </div>
            )}
            
            {linkPagamento && (
              <div style={{
                background: 'linear-gradient(145deg, #f8f9fa 0%, #edf2f7 100%)',
                padding: '1.5rem',
                borderRadius: '16px',
                border: '2px solid #e2e8f0',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '1rem'
                }}>Link de Pagamento:</div>
                <div style={{
                  background: 'white',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '1rem',
                  wordBreak: 'break-all',
                  fontSize: '0.9rem',
                  color: '#2d3748'
                }}>{linkPagamento}</div>
                <button
                  onClick={() => window.open(linkPagamento, '_blank')}
                  style={{
                    background: 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  🔗 Abrir Link de Pagamento
                </button>
              </div>
            )}
            
            {/* E-book selecionado */}
            <div style={{
               background: 'linear-gradient(145deg, #f8f9fa 0%, #edf2f7 100%)',
               padding: '1.5rem',
               borderRadius: '16px',
               border: 'none',
               marginBottom: '2rem',
               textAlign: 'center',
               position: 'relative',
               overflow: 'hidden'
             }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%)'
              }}></div>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#4a5568',
                marginBottom: '0.5rem',
                position: 'relative',
                zIndex: 1
              }}>Produto Selecionado</div>
              <div style={{
                fontSize: '1.8rem',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #1E88E5 0%, #1976d2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                position: 'relative',
                zIndex: 1
              }}>E-book da Clara</div>
            </div>
            
            {!loading && !success && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {formasPagamento.map((forma) => (
                  <button
                    key={forma.value}
                    onClick={() => handlePagamento(forma.value)}
                    style={{
                       background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                       border: 'none',
                       borderRadius: '15px',
                       padding: '1.2rem 1.5rem',
                       fontSize: '1.1rem',
                       fontWeight: '600',
                       color: '#2d3748',
                       cursor: 'pointer',
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '0.75rem',
                       position: 'relative',
                       overflow: 'hidden',
                       boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                     }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(30, 136, 229, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)';
                      e.target.style.color = '#2d3748';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>{forma.icon}</span>
                    {forma.label}
                  </button>
                ))}
              </div>
            )}
            
            {loading && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #e2e8f0',
                  borderTop: '4px solid #1e88e5',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }}></div>
                <p style={{
                  fontSize: '1.1rem',
                  color: '#4a5568',
                  fontWeight: '500',
                  margin: 0
                }}>Processando pagamento...</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && (
            <div style={{
               padding: '1.5rem 2rem',
               borderTop: 'none',
               background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
               display: 'flex',
               justifyContent: 'flex-end'
             }}>
              <button
                onClick={handleCloseModal}
                disabled={loading}
                style={{
                  background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = 'linear-gradient(135deg, #5a6268 0%, #495057 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.background = 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
        
        {/* CSS para animação do spinner */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .payment-modal .modal-dialog {
            max-width: 500px;
          }
          .payment-modal .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
          }
        `}</style>
      </Modal>

      {/* Modal de confirmação de pagamento */}
      <Modal 
        show={showConfirmationModal} 
        onHide={() => {
              setShowConfirmationModal(false);
              window.location.href = '/meus-ebooks';
            }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton style={{backgroundColor: '#28a745', color: 'white'}}>
          <Modal.Title>Pagamento Confirmado!</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{textAlign: 'center', padding: '30px'}}>
          <div style={{fontSize: '18px', marginBottom: '20px'}}>
            <strong>Parabéns! Seu pagamento foi aprovado com sucesso!</strong>
          </div>
          <div style={{fontSize: '16px', marginBottom: '20px'}}>
            Seu e-book já está disponível para download.
          </div>
          <div style={{fontSize: '16px', color: '#28a745', fontWeight: 'bold'}}>
            Clique no botão abaixo para acessar seus e-books!
          </div>
        </Modal.Body>
        <Modal.Footer style={{justifyContent: 'center'}}>
          <Button 
            variant="success" 
            size="lg" 
            onClick={() => {
                  setShowConfirmationModal(false);
                  window.location.href = '/meus-ebooks';
                }}
            style={{minWidth: '200px'}}
          >
            Acessar Meus E-books
          </Button>
        </Modal.Footer>
      </Modal>
    </Template>
  );
}

export default ComprarEbooks;